import React, { useContext, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigate, useNavigate } from 'react-router';
import PageHeader from '../../../../../shared/components/PageHeader';
import OperationContext from '../../context';
import ToolkitsContext from '../context';
import { Toolkit } from '../../../../../services/types/Toolkit';
import Section from '../../../../../shared/components/Section';
import { CheckValue } from '../../../../../services/types/Misc';
import EmptyHistory from '../EmptyHistory';
import Context from '../../../../App/MainProvider/context';
import operationService from '../../../../../services/operationService';
import { Button, ConfirmModal, Modal, SpinnerArea } from '../../../../../shared/components';
import { dateTimeFormat, isDraft, safeFind } from '../../../../../shared/misc/utils';
import EspfVersionTable from './EspfVersionTable';
import Role from '../../../../../types/role';
import NotificationContext from '../../../../Notifications/NotificationContext';

const EspfToolkitList: React.FC = () => {
  const { operation } = useContext(OperationContext);
  if (!operation.isEspf) {
    return <Navigate to={`/operations/${operation.number}/toolkit/list`} />;
  }
  const { toolkits, reload: reloadToolkit } = useContext(ToolkitsContext);
  const { user, service, notify, inSuperUserMode, inGuidanceServiceMode } = useContext(Context);
  const { reload: reloadNotifications } = useContext(NotificationContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sortByVersion = (a: Toolkit, b: Toolkit) => b.version - a.version;

  toolkits.sort(sortByVersion);

  const lastVersion: Toolkit[] = toolkits.filter((x) => !x.isDeleted).filter((x, i) => i === 0);
  const lastVersionId = lastVersion && lastVersion.length > 0 ? lastVersion[0].id : 0;
  const otherVersions: Toolkit[] = toolkits.filter((x) => x.id !== lastVersionId);

  const initialState = toolkits.map((toolkit) => ({
    toolkitId: toolkit.id,
    languages: toolkit.documentList.map(
      (doc, i) =>
        ({
          id: toolkit.id.toString() + i.toString(),
          checked: false,
          text: doc,
        } as CheckValue)
    ),
  }));
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(initialState);
  const handleChange = (id: string, value: boolean, toolkitId: number) => {
    const clone = [...state];
    const toolkitLanguages = safeFind(clone, (x) => x.toolkitId === toolkitId);
    const language = safeFind(toolkitLanguages.languages, (x) => x.id === id);
    language.checked = value;
    setState(clone);
  };
  const handleDownloadClick = (toolkitId: number) => {
    const toolkit = toolkits.find((x) => x.id === toolkitId);
    if (!toolkit) {
      return;
    }

    const dto = { toolkitId: toolkit.id, language: 'English', documentType: 'SF' };
    service(() => operationService.downloadToolkitDocument(dto), { loader: setLoading });
  };

  const [showConfirmDelete, setshowConfirmDelete] = useState(false);
  const [toolkitForDelete, setToolkitForDelete] = useState(0);

  const handleDeleteClick = (toolkitId: number) => {
    const toolkit = toolkits.find((x) => x.id === toolkitId);
    if (!toolkit) {
      return;
    }

    setToolkitForDelete(toolkitId);
    setshowConfirmDelete(true);
  };

  const [modalOpened, setModalOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const confirmCreation = () => {
    setModalOpened(false);

    if (lastVersion[0] != null) {
      service(() => operationService.createToolkitDraftFromVersion(lastVersion[0].id), {
        callback: () => {
          setIsSubmitting(false);
          notify('Toolkit', t('successCreatedDraft'), 'success');
          reloadNotifications();
          reloadToolkit();
        },
        loader: setIsSubmitting,
      });
    }
  };

  const canEdit =
    (operation.isMember ||
      user.roleCode === Role.GroupHead ||
      user.roleCode === Role.HSO ||
      inSuperUserMode() ||
      (operation.belongsGS && inGuidanceServiceMode())) &&
    !isDraft(operation);

  const canDelete = user.roleCode === Role.Admin || inSuperUserMode();

  const noPdfCreated =
    toolkits.length === 0 || lastVersion.length === 0 || lastVersion[0].pdfCreatedBy === null;
  const noNewVersionFromDraft = !canEdit || noPdfCreated || isSubmitting;
  const noNewVersion = !canEdit || (lastVersion.length > 0 && noPdfCreated) || isSubmitting;

  return (
    <>
      {lastVersion.length > 0 && (
        <Modal
          visible={modalOpened}
          setVisible={setModalOpened}
          actions={[
            { text: t('cancel') },
            { text: t('createConfirmToolkitDraftBtn'), action: () => confirmCreation() },
          ]}
        >
          <p>
            <b>{t('createToolkitDraftMsgTitle')}</b>
          </p>
          <p>
            {t('createToolkitDraftMsgContent', {
              version: lastVersion[0].version,
              date: dateTimeFormat(lastVersion[0].createdOn),
            })}
          </p>
        </Modal>
      )}
      <SpinnerArea loading={loading || isSubmitting}>
        <Container className="dashboard">
          <PageHeader backTimes={3} title={`Operation ${operation.number} / Toolkit`} />
          <Row>
            <Section
              title={
                <>
                  <h5>{t('currentVersion')}</h5>
                  <div>
                    <Button
                      key="btnNewVersionFromCurrent"
                      className="btn btn-primary px-4 py-1"
                      onClick={() => setModalOpened(true)}
                      text={t('esrrNewFromMostRecent')}
                      disabled={noNewVersionFromDraft}
                    />
                    <Button
                      key="btnNewVersion"
                      className="btn btn-secondary px-4 py-1"
                      text={t('New')}
                      disabled={noNewVersion}
                      onClick={() => {
                        navigate(`/operations/${operation.number}/toolkit/espf/financial-form`);
                      }}
                    />
                  </div>
                </>
              }
            >
              <EspfVersionTable
                operation={operation}
                toolkits={lastVersion}
                availableLanguages={initialState.filter(
                  (x) => lastVersion[0] != null && x.toolkitId === lastVersion[0].id
                )}
                handleChange={handleChange}
                handleDownloadClick={handleDownloadClick}
                handleDeleteClick={canDelete ? handleDeleteClick : undefined}
                className="last-version-content"
              />

              {toolkits.length === 0 && (
                <div>
                  <div className="m-3 d-flex justify-content-center">
                    <div className="circle-base-big">
                      <div className="circle-inner">
                        <FontAwesomeIcon icon="file-word" />
                      </div>
                    </div>
                  </div>
                  <div className="m-3 d-flex justify-content-center">
                    <h1 className="page-title inner-title">
                      {t('espfNotToolkitYet', { number: operation.number })}
                    </h1>
                  </div>
                </div>
              )}
            </Section>
            {toolkits.length > 0 && (
              <Section title={t('history')}>
                {otherVersions.length > 0 ? (
                  <EspfVersionTable
                    operation={operation}
                    toolkits={otherVersions}
                    availableLanguages={initialState.filter(
                      (x) => lastVersion.length === 0 || x.toolkitId !== lastVersion[0].id
                    )}
                    handleChange={handleChange}
                    handleDownloadClick={handleDownloadClick}
                    className="history-content"
                  />
                ) : (
                  <EmptyHistory />
                )}
              </Section>
            )}
          </Row>
        </Container>
      </SpinnerArea>
      <ConfirmModal
        visible={showConfirmDelete}
        buttonText={t('confirm')}
        setVisible={setshowConfirmDelete}
        action={() => {
          service(() => operationService.deleteToolkit(toolkitForDelete), {
            callback: () => {
              notify(
                t(`Toolkit`),
                t(`success-delete-remember-convergence-toolkit`),
                'warning',
                'exclamation-triangle'
              );
            },
          }).finally(() => {
            reloadToolkit();
          });
        }}
      >
        {t('confirmDeleteToolkitMessage')}
      </ConfirmModal>
    </>
  );
};

export default EspfToolkitList;
